import React, { useCallback, useEffect, useState } from "react";
import GraphicsOrders from "components/Graphics/Orders";

import dayjs, { Dayjs } from "dayjs";
import { Button, Divider, Grid } from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { DatePicker } from "ctech-react-components";
import { handleGetReportDataRequest } from "store/modules/Report/thunk.store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useAuth } from "context/AuthContext";
import * as Styled from "./styles";
import Card from "./components/Card";
import TotalValue from "./components/TotalValue";
import SuccessRateEvaluation from "./components/SuccessRateEvaluation";
import TrackedDeliveries from "./components/TrackedDeliveries";
import OrdersDeliveredTime from "./components/OrdersDeliveredTime";
import Orders from "./components/Orders";
import { isAfter } from "date-fns";

const ReportsDashboard = () => {
  const [from, setFrom] = useState<Dayjs | null>(
    dayjs(new Date()).subtract(1, "day"),
  );
  const [to, setTo] = useState<Dayjs | null>(dayjs(from).add(59, "day"));

  const dispatch = useAppDispatch();
  const { dealerId } = useAuth();

  const { data: reportData } = useAppSelector((state) => state.report);

  const handleGetDashboardData = useCallback(
    async (dateFrom?: string, dateTo?: string) => {
      await dispatch(
        handleGetReportDataRequest({ dealerId, dateFrom, dateTo }),
      );
    },
    [dealerId, dispatch],
  );

  useEffect(() => {
    handleGetDashboardData(
      from?.format("YYYY-MM-DD"),
      to?.format("YYYY-MM-DD"),
    );
  }, [from, handleGetDashboardData, to]);

  const dateGreaterThanCurrent = isAfter(
    new Date(dayjs(from).add(59, "day").toString()),
    new Date(),
  );

  useEffect(() => {
    setTo(
      dateGreaterThanCurrent ? dayjs(new Date()) : dayjs(from).add(59, "day"),
    );
  }, [from, dateGreaterThanCurrent]);

  return (
    <Styled.Container>
      <Styled.Content>
        <Grid container columns={12} spacing={2}>
          <Grid
            item
            xs={6}
            md={8}
            sm={12}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <DatePicker
              label="De"
              value={from}
              handleChange={setFrom}
              maxDate={dayjs(new Date())}
              fullWidth
              disabledInput
            />
            <ArrowForwardIcon sx={{ marginLeft: 1, marginRight: 1 }} />
            <DatePicker
              label="Até"
              value={to}
              handleChange={setTo}
              minDate={dayjs(from)}
              maxDate={
                dateGreaterThanCurrent
                  ? dayjs(new Date())
                  : dayjs(from).add(59, "day")
              }
              fullWidth
              disabledInput
            />
          </Grid>
          <Grid item xs={6} md={4} sm={12} display="flex" alignItems="center">
            <Button
              variant="outlined"
              fullWidth
              size="large"
              sx={{ borderRadius: 8 }}
              onClick={() =>
                handleGetDashboardData(
                  from?.format("YYYY-MM-DD"),
                  to?.format("YYYY-MM-DD"),
                )
              }
            >
              Visualizar Informações
            </Button>
          </Grid>
        </Grid>
        <Divider sx={{ marginTop: 3, marginBottom: 3 }} />
        <Grid container columns={12} spacing={2}>
          <Grid item xs={6} md={8} sm={12}>
            <GraphicsOrders
              data={reportData?.dailyPerformance || []}
              width="100%"
              height={450}
            />
            <Card>
              <Orders
                data={{
                  received: reportData?.ordersReceived || 0,
                  delivered: reportData?.ordersDelivered || 0,
                  canceled: {
                    user: reportData?.ordersCancelledByUser || 0,
                    dealer: reportData?.ordersCancelledByDealer || 0,
                    automatic: reportData?.ordersCancelledBySystem || 0,
                  },
                  declined: reportData?.ordersDenied || 0,
                }}
              />
            </Card>
          </Grid>
          <Grid
            item
            xs={6}
            md={4}
            sm={12}
            rowGap={2}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Card>
              <TotalValue value={reportData?.totalAmount ?? 0} />
            </Card>
            <Card>
              <SuccessRateEvaluation
                data={{
                  efficiencyRate: reportData?.efficiencyRate ?? 0,
                  dealerEfficiencyRate: reportData?.dealerEfficiencyRate ?? 0,
                  evaluation: reportData?.averageRating ?? 0,
                }}
              />
            </Card>
            <Card>
              <TrackedDeliveries
                data={{
                  porcentage: reportData?.trackedDeliveryRate ?? 0,
                  deliveriesWithDriver: reportData?.trackedDelivery ?? 0,
                  driverlessDeliveries: reportData?.untrackedDelivery ?? 0,
                }}
              />
            </Card>
            <Card>
              <OrdersDeliveredTime
                data={{
                  porcentage: reportData?.deliveredInTimeRate ?? 0,
                  estimatedAverageTime: reportData?.averageEstimatedTime ?? 0,
                  averageTimePerformed: reportData?.averageDeliveryTime ?? 0,
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Styled.Content>
    </Styled.Container>
  );
};

export default ReportsDashboard;
